import React, {useMemo} from 'react';
import {
    FormHelpHeader,
    SearchInput,
    ListTitle,
    TextSection,
    Item,
    VideoItem,
    EmptyStateNoData,
    EmptyStateXpert,
    EmptyStateSearch,
    Loader,
    XpertError,
    ListFooter
} from '../FormHelpUI';
import texts from '../../texts';
import styles from '../../styles/FormHelpDialog.module.scss';
import {Flex, PriorityIcon, Scrollbar, Separator} from '@priority-software/priority-style-react';
import {observer} from 'mobx-react';
import {sendMonitor} from '../../../../utils/monitoring';
import {DocType, FormHelp, FormHelpPageState, Pages} from '../../types';
import {openInXpertByFormName} from '../../actions/helpActions';
import {isEmptyState} from '../../utils/emptyState';

export type MainPageProp = {
    isNewApi?: boolean;
    query?: string;
    formHelp?: FormHelp;
    isLoadingHelpData?: boolean;
    addHelpPage?: (page: FormHelpPageState) => void;
    setHelpPageQuery?: (query: string) => void;
    title?: string;
};

export const MainPage = observer(
    ({
        isNewApi,
        query = '',
        formHelp,
        isLoadingHelpData,
        addHelpPage,
        setHelpPageQuery,
        title
    }: MainPageProp) => {
        const helpData = formHelp;
        const faqs =
            helpData?.faqs?.filter(faq => faq.text?.toLowerCase().includes(query.toLowerCase())) ||
            [];
        const documents =
            helpData?.documents?.filter(doc =>
                doc.text?.toLowerCase().includes(query.toLowerCase())
            ) || [];
        const videos =
            helpData?.videos?.filter(video =>
                video.text?.toLowerCase().includes(query.toLowerCase())
            ) || [];

        const noSearchResults =
            query !== '' && videos?.length === 0 && documents?.length === 0 && faqs?.length === 0;

        const hasXpertData =
            !helpData?.hasXpertError &&
            !isLoadingHelpData &&
            (helpData?.videos?.length > 0 ||
                helpData?.documents?.length > 0 ||
                helpData?.faqs?.length > 0);

        const helpDataTextTrimed = helpData?.text.text.trim();
        const emptyFormHelpData = !helpData?.helpContent && isEmptyState(helpDataTextTrimed);

        const hasNoData =
            !isLoadingHelpData && !hasXpertData && !helpData?.text?.text && !helpData?.helpContent;

        const Lists = (
            <>
                {videos.length > 0 && (
                    <>
                        <ListTitle title={texts.videos} icon={PriorityIcon.play} />
                        <Flex direction="row" justifyContent="flex-start">
                            {videos?.slice(0, 2).map((video, index) => (
                                <VideoItem key={`main_video_${index}`} query={query} item={video} />
                            ))}
                        </Flex>
                        <ListFooter
                            onViewAll={
                                videos?.length > 2
                                    ? () => {
                                          addHelpPage({
                                              page: Pages.list,
                                              docType: DocType.Video
                                          });
                                          sendMonitor('form help - view all Videos');
                                      }
                                    : null
                            }
                        />
                        <Separator align="vertical" />
                    </>
                )}
                {documents.length > 0 && (
                    <>
                        <ListTitle title={texts.documents} icon={PriorityIcon.NewDoc} />
                        {documents?.slice(0, 3).map((item, index) => (
                            <Item
                                key={`main_docs_${index}`}
                                query={query}
                                item={item}
                                docType={DocType.Document}
                                onClick={() => addHelpPage({page: Pages.details, id: item.id})}
                            />
                        ))}
                        <ListFooter
                            onViewAll={
                                documents?.length > 3
                                    ? () => {
                                          addHelpPage({
                                              page: Pages.list,
                                              docType: DocType.Document
                                          });
                                          sendMonitor('form help - view all Docs');
                                      }
                                    : null
                            }
                        />
                        <Separator align="vertical" />
                    </>
                )}
                {faqs.length > 0 && (
                    <>
                        <ListTitle title={texts.faqs} icon={PriorityIcon.questionCircle} />
                        {faqs?.slice(0, 3).map((item, index) => (
                            <Item
                                key={`main_faqs_${index}`}
                                item={item}
                                query={query}
                                docType={DocType.FAQ}
                                onClick={() => addHelpPage({page: Pages.details, id: item.id})}
                            />
                        ))}
                        <ListFooter
                            onViewAll={
                                faqs?.length > 3
                                    ? () => {
                                          addHelpPage({
                                              page: Pages.list,
                                              docType: DocType.FAQ,
                                              query: query
                                          });
                                          sendMonitor('form help - view all FAQs');
                                      }
                                    : null
                            }
                        />
                        <Separator align="vertical" />
                    </>
                )}
            </>
        );

        const textData = useMemo(() => {
            if (helpData?.helpContent) {
                return null;
            } else {
                return isNewApi
                    ? helpData?.text
                    : {...helpData?.text, text: helpData?.text?.text.trim()};
            }
        }, [isNewApi, helpData?.text, helpData?.helpContent]);

        const content = !isLoadingHelpData ? (
            <>
                {!emptyFormHelpData ? (
                    textData ? (
                        <TextSection textData={textData} hasXpertData={hasXpertData} />
                    ) : (
                        <>{helpData?.helpContent}</>
                    )
                ) : (
                    <></>
                )}
                {hasXpertData ? (
                    <>
                        <SearchInput
                            value={query}
                            onClear={() => setHelpPageQuery('')}
                            onChange={text => setHelpPageQuery(text)}
                            placeholder={texts.inputMainPlaceholder}
                        />
                        {noSearchResults ? (
                            <EmptyStateSearch onClick={openInXpertByFormName} query={query} />
                        ) : (
                            Lists
                        )}
                    </>
                ) : helpData?.hasXpertError ? (
                    <XpertError />
                ) : (
                    <EmptyStateXpert isTextOnly={!hasXpertData && !emptyFormHelpData} />
                )}
            </>
        ) : (
            <Loader />
        );

        return (
            <Flex
                direction="column"
                justifyContent="flex-start"
                componentClasses={styles.container}
            >
                <FormHelpHeader largeHeader topTitle={texts.help} bottomTitle={title} />
                <Scrollbar componentClasses={styles.scrollContainer}>
                    <div className={styles.content}>
                        {hasNoData ? <EmptyStateNoData /> : content}
                    </div>
                </Scrollbar>
            </Flex>
        );
    }
);
